import { useState, useEffect } from 'react'
import { designService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

export default function History() {
  const [designs, setDesigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [selectedDesign, setSelectedDesign] = useState(null)
  const [selectedDesigns, setSelectedDesigns] = useState(new Set())
  const [downloading, setDownloading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadDesigns()
  }, [page])

  const loadDesigns = async () => {
    try {
      setLoading(true)
      const response = await designService.listDesigns(page * 20, 20)
      console.log('Design history response:', response)
      console.log('Design data:', response.data)

      // Handle both array response and wrapped response
      const designList = Array.isArray(response.data) ? response.data : response.data?.data || []
      setDesigns(designList)
      setSelectedDesigns(new Set())
      setError(null)
    } catch (err) {
      console.error('Error loading designs:', err)
      setError(`Failed to load design history: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectDesign = (designId) => {
    const newSelected = new Set(selectedDesigns)
    if (newSelected.has(designId)) {
      newSelected.delete(designId)
    } else {
      newSelected.add(designId)
    }
    setSelectedDesigns(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedDesigns.size === designs.length) {
      setSelectedDesigns(new Set())
    } else {
      setSelectedDesigns(new Set(designs.map(d => d.id)))
    }
  }

  const downloadSingleDesign = async (design) => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        alert('Please login first')
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'
      const response = await fetch(`${apiUrl}/designs/${design.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `design-${design.id}-${design.item1_name}.jpg`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Error downloading design:', err)
      alert('Failed to download design: ' + err.message)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedDesigns.size === 0) {
      alert('Please select at least one design to delete')
      return
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedDesigns.size} design(s)? This action cannot be undone.`
    )

    if (!confirmDelete) return

    try {
      setDeleting(true)
      const designIds = Array.from(selectedDesigns)

      const response = await designService.batchDeleteDesigns(designIds)
      console.log('Delete response:', response)

      if (response.data.success) {
        alert(`✅ Successfully deleted ${response.data.deleted_count} design(s)`)
        setSelectedDesigns(new Set())
        loadDesigns()
      } else {
        alert('Failed to delete designs')
      }
    } catch (err) {
      console.error('Error deleting designs:', err)
      alert('Failed to delete designs: ' + (err.response?.data?.detail || err.message))
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDownload = async () => {
    if (selectedDesigns.size === 0) {
      alert('Please select at least one design to download')
      return
    }

    try {
      setDownloading(true)

      // If only one design, download directly
      if (selectedDesigns.size === 1) {
        const designId = Array.from(selectedDesigns)[0]
        const design = designs.find(d => d.id === designId)
        await downloadSingleDesign(design)
        setDownloading(false)
        return
      }

      // For multiple designs, create a zip file
      const token = localStorage.getItem('access_token')
      if (!token) {
        alert('Please login first')
        setDownloading(false)
        return
      }

      const JSZip = (await import('jszip')).default
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

      const zip = new JSZip()
      const folder = zip.folder('cargills-designs')

      for (const designId of selectedDesigns) {
        const design = designs.find(d => d.id === designId)
        if (design && design.rendered_image_url) {
          try {
            const response = await fetch(`${apiUrl}/designs/${design.id}/download`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
            if (response.ok) {
              const blob = await response.blob()
              const filename = `design-${design.id}-${design.item1_name}.jpg`
              folder.file(filename, blob)
            }
          } catch (err) {
            console.error('Error fetching design:', err)
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const url = window.URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `cargills-designs-${Date.now()}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setSelectedDesigns(new Set())
    } catch (err) {
      console.error('Error in bulk download:', err)
      alert('Failed to download designs')
    } finally {
      setDownloading(false)
    }
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="history-page">
      <div className="history-header">
        <h2>Design History</h2>
        {selectedDesigns.size > 0 && (
          <div className="bulk-actions">
            <span className="selection-count">{selectedDesigns.size} selected</span>
            <button
              className="bulk-download-btn"
              onClick={handleBulkDownload}
              disabled={downloading}
            >
              {downloading ? '⏳ Downloading...' : '📥 Download'}
            </button>
            <button
              className="bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? '⏳ Deleting...' : '🗑️ Delete'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {designs.length === 0 ? (
        <div className="empty-state">
          <p>No designs generated yet</p>
          <p className="sub-text">Create designs using Manual Input or Bulk Upload</p>
        </div>
      ) : (
        <>
          {designs.length > 0 && (
            <div className="select-all-bar">
              <label className="select-all-checkbox">
                <input
                  type="checkbox"
                  checked={selectedDesigns.size === designs.length && designs.length > 0}
                  onChange={handleSelectAll}
                />
                <span>Select All ({designs.length})</span>
              </label>
            </div>
          )}

          <div className="designs-grid">
            {designs.map(design => (
              <div
                key={design.id}
                className={`design-card ${selectedDesigns.has(design.id) ? 'selected' : ''}`}
              >
                <div className="card-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedDesigns.has(design.id)}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleSelectDesign(design.id)
                    }}
                  />
                </div>

                <div
                  className="design-image"
                  onClick={() => setSelectedDesign(design)}
                  style={{ cursor: 'pointer' }}
                >
                {design.rendered_image_url ? (
                  <>
                    <img
                      src={design.rendered_image_url}
                      alt={design.item1_name}
                      onError={(e) => {
                        console.error('Image failed to load:', design.rendered_image_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  </>
                ) : (
                  <div className="no-image">
                    <p>🖼️</p>
                    <p>Rendering...</p>
                  </div>
                )}
              </div>
              <div className="design-info">
                <h3>{design.item1_name}</h3>
                {design.item2_name && <p className="item2-name">+ {design.item2_name}</p>}
                <p className="template-type">
                  {design.template_type === 'single' ? '1️⃣ Single' : '2️⃣ Double'}
                </p>
                <p className="status" data-status={design.status}>
                  {design.status === 'completed' ? '✅ Done' : design.status}
                </p>
                <p className="date">
                  {new Date(design.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            ))}
          </div>
        </>
      )}

      {selectedDesign && (
        <div className="design-detail-modal" onClick={() => setSelectedDesign(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedDesign(null)}>✕</button>

            <div className="modal-body">
              <div className="modal-image">
                {selectedDesign.rendered_image_url ? (
                  <img
                    src={selectedDesign.rendered_image_url}
                    alt={selectedDesign.item1_name}
                  />
                ) : (
                  <div className="no-image">No image</div>
                )}
              </div>

              <div className="modal-details">
                <h2>Design Details</h2>

                <div className="detail-section">
                  <h3>Item 1</h3>
                  <p><strong>Product:</strong> {selectedDesign.item1_name}</p>
                  {selectedDesign.item1_quantity && (
                    <p><strong>Quantity:</strong> {selectedDesign.item1_quantity}</p>
                  )}
                  {selectedDesign.item1_price && (
                    <p><strong>Price:</strong> Rs. {selectedDesign.item1_price}</p>
                  )}
                </div>

                {selectedDesign.item2_name && (
                  <div className="detail-section">
                    <h3>Item 2</h3>
                    <p><strong>Product:</strong> {selectedDesign.item2_name}</p>
                    {selectedDesign.item2_quantity && (
                      <p><strong>Quantity:</strong> {selectedDesign.item2_quantity}</p>
                    )}
                    {selectedDesign.item2_price && (
                      <p><strong>Price:</strong> Rs. {selectedDesign.item2_price}</p>
                    )}
                  </div>
                )}

                <div className="detail-section">
                  <h3>Template Info</h3>
                  <p><strong>Type:</strong> {selectedDesign.template_type === 'single' ? '1️⃣ Single Item' : '2️⃣ Double Item'}</p>
                  <p><strong>Status:</strong> <span className={`status-badge ${selectedDesign.status}`}>{selectedDesign.status}</span></p>
                  <p><strong>Created:</strong> {new Date(selectedDesign.created_at).toLocaleString()}</p>
                </div>

                {selectedDesign.rendered_image_url && (
                  <div className="detail-section">
                    <button
                      className="download-btn"
                      onClick={() => downloadSingleDesign(selectedDesign)}
                    >
                      📥 Download Design
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pagination">
        <button
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous
        </button>
        <span>Page {page + 1}</span>
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={designs.length < 20}
        >
          Next
        </button>
      </div>
    </div>
  )
}
